import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format } from "date-fns";
import {
  BarChart3,
  Bell,
  Building2,
  Calendar,
  ClipboardList,
  FileText,
  Hash,
  HeartPulse,
  ListTodo,
  Lock,
  MapPin,
  Pill,
  Search,
  TestTube,
  Scan,
  ShieldAlert,
  UserPlus,
  Contact,
  Users,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { canAccessMainTab, canAccessPath } from "@/lib/maintabAccess";
import { api } from "@/lib/orgApi";
import { listCptCodes } from "@/lib/cptApi";
import { listMedications } from "@/lib/medicationApi";
import { listLabs } from "@/lib/labApi";
import { listRadiology } from "@/lib/radiologyApi";
import { listPhysicianReferrals } from "@/lib/physicianReferralApi";
import { listReferringProviders } from "@/lib/referringProviderApi";
import { DASH } from "@/constants/testIds";

const ROLE_LABELS = {
  administrator: "Administrator",
  front_desk: "Front Desk",
  medical_assistant: "Medical Assistant",
  nurse: "Nurse",
  np: "NP",
  pa: "PA",
  md_do: "MD/DO",
  biller: "Biller",
};

const QUICK_ACCESS = [
  {
    title: "Patients",
    description: "Find and manage patient records.",
    to: "/patients",
    icon: Users,
    action: "Open Patients",
    testId: DASH.cardPatients,
  },
  {
    title: "Encounters",
    description: "Review and manage clinical encounters.",
    to: "/encounters",
    icon: ClipboardList,
    action: "Open Encounters",
    testId: DASH.cardEncounters,
  },
  {
    title: "ICD-10-CM",
    description: "Search the read-only diagnosis terminology master.",
    to: "/icd10-cm",
    icon: Search,
    action: "Search ICD-10-CM",
    testId: DASH.cardIcd10,
  },
  {
    title: "CPT",
    description: "Pain medicine procedure terminology",
    to: "/cpt",
    icon: FileText,
    action: "View CPT",
    testId: DASH.cardCpt,
  },
  {
    title: "Px Master",
    description: "Central procedure terminology for Surgical History and future procedures.",
    to: "/settings/px-master",
    icon: FileText,
    action: "Open Px Master",
    testId: DASH.cardPxMaster,
    adminOnly: true,
  },
  {
    title: "Allergen Master",
    description: "Allergen / contraindication terminology for Allergy Lookup.",
    to: "/settings/allergen-master",
    icon: ShieldAlert,
    action: "Open Allergen Master",
    testId: DASH.cardAllergenMaster,
    adminOnly: true,
  },
  {
    title: "Medications",
    description: "Pain medicine medication terminology",
    to: "/medications",
    icon: Pill,
    action: "View Medications",
    testId: DASH.cardMedications,
  },
  {
    title: "Labs",
    description: "Laboratory test terminology",
    to: "/labs",
    icon: TestTube,
    action: "View Labs",
    testId: DASH.cardLabs,
  },
  {
    title: "Radiology",
    description: "Radiology study terminology",
    to: "/radiology",
    icon: Scan,
    action: "View Radiology",
    testId: DASH.cardRadiology,
  },
  {
    title: "Physician Referrals",
    description: "Physician specialty referral terminology",
    to: "/physician-referrals",
    icon: UserPlus,
    action: "View Physician Referrals",
    testId: DASH.cardPhysicianReferrals,
  },
  {
    title: "Referring Providers",
    description: "External referring physician directory",
    to: "/referring-providers",
    icon: Contact,
    action: "View Referring Providers",
    testId: DASH.cardReferringProviders,
  },
  {
    title: "Placeholder Vocabulary",
    description: "Read-only list of dynamic @placeholder@ tokens.",
    to: "/placeholder-vocabulary",
    icon: Hash,
    action: "Open Placeholder Vocabulary",
    testId: DASH.cardPlaceholderVocabulary,
  },
  {
    title: "Reports",
    description: "Run reports and view analytics.",
    icon: BarChart3,
    action: "View Reports",
    planned: true,
    testId: DASH.cardReports,
  },
];

const CLINICAL_FOUNDATION = [
  { label: "Organization", to: "/organizations", icon: Building2 },
  { label: "Location", to: "/locations", icon: MapPin },
  { label: "Patient", to: "/patients", icon: Users },
  { label: "Encounter", to: "/encounters", icon: ClipboardList },
  { label: "ICD-10-CM Diagnosis Master", to: "/icd10-cm", icon: HeartPulse },
  { label: "Px Master", to: "/settings/px-master", icon: FileText, adminOnly: true },
  { label: "Allergen Master", to: "/settings/allergen-master", icon: ShieldAlert, adminOnly: true },
];

const NEXT_AREAS = [
  { title: "Orders", description: "Clinical order entry and tracking.", icon: ClipboardList },
  { title: "Role-specific workflows", description: "Task flows tailored to Front Desk, MA, nursing, providers, and billing.", icon: Bell },
];

function primaryRoleLabel(roles) {
  const list = roles || [];
  const preferred = ["md_do", "np", "pa", "nurse", "administrator", "medical_assistant", "front_desk", "biller"];
  const match = preferred.find((role) => list.includes(role)) || list[0];
  return match ? (ROLE_LABELS[match] || match) : null;
}

function formatCount(value) {
  if (value == null) return "—";
  return String(value);
}

function WelcomePulse() {
  return (
    <svg className="emr-dash__pulse" width="120" height="36" viewBox="0 0 120 36" fill="none" aria-hidden="true">
      <path
        d="M1 20 H18 L24 8 L32 28 L40 14 L46 20 H62 L68 6 L76 30 L84 16 L90 20 H119"
        stroke="#42A5F5"
        strokeWidth="1.75"
        strokeLinejoin="round"
        strokeLinecap="square"
      />
    </svg>
  );
}

function ModuleCard({ title, description, to, icon: Icon, action, testId, planned }) {
  const body = (
    <div className="card-content">
      <div className="emr-dash-module__icon" aria-hidden="true">
        <Icon className="h-4 w-4" />
      </div>
      <span className="card-title">{title}</span>
      <p>{description}</p>
      <span className="emr-dash-module__action">
        {action} <span aria-hidden="true">→</span>
      </span>
    </div>
  );

  if (planned) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={() => toast.info("Reports is a planned development area.")}
        className="card emr-dash-module"
      >
        {body}
      </button>
    );
  }

  return (
    <Link to={to} data-testid={testId} className="card emr-dash-module">
      {body}
    </Link>
  );
}

function moduleDescription(module, counts) {
  if (module.title === "CPT" && counts.cpt != null) {
    return (
      <>
        {counts.cpt} active procedure codes
        <br />
        Pain medicine procedure terminology
      </>
    );
  }
  if (module.title === "Medications" && counts.medications != null) {
    return (
      <>
        {counts.medications} active medications
        <br />
        Pain medicine medication terminology
      </>
    );
  }
  if (module.title === "Labs" && counts.labs != null) {
    return (
      <>
        {counts.labs} active laboratory tests
        <br />
        Laboratory test terminology
      </>
    );
  }
  if (module.title === "Radiology" && counts.radiology != null) {
    return (
      <>
        {counts.radiology} active imaging studies
        <br />
        Radiology study terminology
      </>
    );
  }
  if (module.title === "Physician Referrals" && counts.physicianReferrals != null) {
    return (
      <>
        {counts.physicianReferrals} active physician specialties
        <br />
        Physician specialty referral terminology
      </>
    );
  }
  if (module.title === "Referring Providers" && counts.referringProviders != null) {
    return (
      <>
        {counts.referringProviders} active referring providers
        <br />
        External referring physician directory
      </>
    );
  }
  return module.description;
}

export default function DashboardPage() {
  const { user, context, isAdmin } = useAuth();
  const tabOpts = { isAdmin };
  const canSeePatients = canAccessMainTab(user?.maintabAccess, "patients", tabOpts);
  const canSeeEncounters = canAccessMainTab(user?.maintabAccess, "encounters", tabOpts);
  const canSeeCpt = canAccessMainTab(user?.maintabAccess, "cpt", tabOpts);
  const canSeeMedications = canAccessMainTab(user?.maintabAccess, "medications", tabOpts);
  const canSeeLabs = canAccessMainTab(user?.maintabAccess, "labs", tabOpts);
  const canSeeRadiology = canAccessMainTab(user?.maintabAccess, "radiology", tabOpts);
  const canSeeReferrals = canAccessMainTab(user?.maintabAccess, "referrals", tabOpts);
  const canSeeReferring = canAccessMainTab(user?.maintabAccess, "referring-providers", tabOpts);
  const [selectedDate, setSelectedDate] = useState(() => format(new Date(), "yyyy-MM-dd"));
  const [counts, setCounts] = useState({ patients: null, encounters: null, users: null, cpt: null, medications: null, labs: null, radiology: null, physicianReferrals: null, referringProviders: null });

  const displayName = user?.displayName || user?.email || "User";
  const roleLabel = primaryRoleLabel(user?.roles);
  const orgName = context?.organization?.name;
  const locName = context?.location?.name;
  const dateLabel = useMemo(() => {
    try {
      return format(new Date(`${selectedDate}T00:00:00`), "MMMM d, yyyy");
    } catch {
      return selectedDate;
    }
  }, [selectedDate]);

  useEffect(() => {
    let cancelled = false;
    const requests = [
      canSeePatients ? api.get("/patients").then((r) => (r.data.items || []).length) : Promise.resolve(null),
      canSeeEncounters ? api.get("/encounters").then((r) => (r.data.items || []).length) : Promise.resolve(null),
    ];
    if (isAdmin) {
      requests.push(
        api.get("/users").then((r) =>
          (r.data.items || []).filter((u) => u.active !== false).length,
        ),
      );
    }
    Promise.all(requests)
      .then(([patients, encounters, users]) => {
        if (!cancelled) {
          setCounts((prev) => ({ ...prev, patients, encounters, users: users ?? null }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCounts((prev) => ({ ...prev, patients: null, encounters: null, users: null }));
        }
      });
    if (canSeeCpt) {
      listCptCodes({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, cpt: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, cpt: null }));
        });
    }
    if (canSeeMedications) {
      listMedications({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, medications: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, medications: null }));
        });
    }
    if (canSeeLabs) {
      listLabs({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, labs: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, labs: null }));
        });
    }
    if (canSeeRadiology) {
      listRadiology({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, radiology: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, radiology: null }));
        });
    }
    if (canSeeReferrals) {
      listPhysicianReferrals({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, physicianReferrals: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, physicianReferrals: null }));
        });
    }
    if (canSeeReferring) {
      listReferringProviders({ active: true })
        .then((data) => {
          if (!cancelled) {
            const total = data?.total ?? (data?.items || []).length;
            setCounts((prev) => ({ ...prev, referringProviders: total }));
          }
        })
        .catch(() => {
          if (!cancelled) setCounts((prev) => ({ ...prev, referringProviders: null }));
        });
    }
    return () => {
      cancelled = true;
    };
  }, [isAdmin, canSeePatients, canSeeEncounters, canSeeCpt, canSeeMedications, canSeeLabs, canSeeRadiology, canSeeReferrals, canSeeReferring]);

  return (
    <div data-testid={DASH.page} className="emr-dash">
      <div className="emr-dash__pagehead">
        <div>
          <h1 data-testid={DASH.title} className="emr-dash__title">
            Dashboard
          </h1>
          <p className="emr-dash__subtitle">
            Your starting point for the V3 FHIR-first EMR foundation.
          </p>
        </div>
        <label className="emr-dash__date">
          <Calendar className="h-4 w-4" aria-hidden="true" />
          <span>{dateLabel}</span>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="emr-dash__date-input"
            aria-label="Select date"
          />
        </label>
      </div>

      <div className="emr-dash__workspace">
        <div className="emr-dash__top">
          <section data-testid={DASH.welcome} className="card emr-dash__section emr-dash__welcome">
            <div className="card-content">
              <h2 className="emr-dash__mod-title">Welcome</h2>
              <div className="emr-dash__welcome-body">
                <div className="emr-dash__welcome-copy">
                  <p className="emr-dash__kicker">Welcome back</p>
                  <h3 className="emr-dash__name">{displayName}</h3>
                  <div className="emr-dash__meta">
                    {user?.email && <span>{user.email}</span>}
                    {roleLabel && <span className="emr-dash__role">{roleLabel}</span>}
                  </div>
                  {(orgName || locName) && (
                    <div className="emr-dash__orgrow">
                      {orgName && (
                        <span>
                          <Building2 className="h-4 w-4" aria-hidden="true" />
                          {orgName}
                        </span>
                      )}
                      {locName && (
                        <span>
                          <MapPin className="h-4 w-4" aria-hidden="true" />
                          {locName}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <div className="emr-dash__welcome-aside">
                  <WelcomePulse />
                  <p className="emr-dash__quote">“Better Care. A Healthier Tomorrow.”</p>
                </div>
              </div>
            </div>
          </section>

          <div className="emr-dash__top-side">
            <section className="card emr-dash__section emr-dash-panel">
              <div className="card-content">
                <h2 className="emr-dash__mod-title">At a glance</h2>
                <div className="emr-dash-glance">
                  {canSeePatients ? (
                    <Link to="/patients" className="emr-dash-glance__row">
                      <span className="emr-dash-glance__label">
                        <span className="emr-dash-glance__icon emr-dash-glance__icon--patients">
                          <Users className="h-3.5 w-3.5" />
                        </span>
                        Patients
                      </span>
                      <span className="emr-dash-glance__value">{formatCount(counts.patients)}</span>
                    </Link>
                  ) : null}
                  {canSeeEncounters ? (
                    <Link to="/encounters" className="emr-dash-glance__row">
                      <span className="emr-dash-glance__label">
                        <span className="emr-dash-glance__icon emr-dash-glance__icon--encounters">
                          <ClipboardList className="h-3.5 w-3.5" />
                        </span>
                        Encounters
                      </span>
                      <span className="emr-dash-glance__value">{formatCount(counts.encounters)}</span>
                    </Link>
                  ) : null}
                  <div className="emr-dash-glance__row">
                    <span className="emr-dash-glance__label">
                      <span className="emr-dash-glance__icon emr-dash-glance__icon--worklists">
                        <ListTodo className="h-3.5 w-3.5" />
                      </span>
                      Active Worklists
                    </span>
                    <span className="emr-dash-glance__value is-empty">—</span>
                  </div>
                  <div className="emr-dash-glance__row">
                    <span className="emr-dash-glance__label">
                      <span className="emr-dash-glance__icon emr-dash-glance__icon--alerts">
                        <Bell className="h-3.5 w-3.5" />
                      </span>
                      System Alerts
                    </span>
                    <span className="emr-dash-glance__value is-empty">—</span>
                  </div>
                </div>
              </div>
            </section>

            <section className="card emr-dash__section emr-dash-panel">
              <div className="card-content">
                <h2 className="emr-dash__mod-title">Recent activity</h2>
                <div className="emr-dash-activity-wrap">
                  <table className="striped highlight emr-dash-activity">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>User</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td colSpan={3} className="emr-dash-empty">
                          Activity tracking is not available yet.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          </div>
        </div>

        <section className="card emr-dash__section emr-dash__quick-mod">
          <div className="card-content">
            <h2 className="emr-dash__mod-title">Quick access</h2>
            <p className="emr-dash__section-help">Open the part of the EMR you need.</p>
            <div className="emr-dash-quick">
              {QUICK_ACCESS.filter((module) => (!module.adminOnly || isAdmin) && canAccessPath(user?.maintabAccess, module.to, tabOpts)).map((module) => (
                <ModuleCard
                  key={module.title}
                  {...module}
                  description={moduleDescription(module, counts)}
                />
              ))}
              {isAdmin && (
                <ModuleCard
                  title="Users"
                  description={
                    counts.users != null
                      ? `${counts.users} active users. Physicians, Front Desk, Billing & Administration`
                      : "Physicians, Front Desk, Billing & Administration"
                  }
                  to="/users"
                  icon={Users}
                  action="View Users"
                  testId={DASH.cardUsers}
                />
              )}
              {isAdmin && (
                <ModuleCard
                  title="Access Control"
                  description="Set organization default and individual user subtab access."
                  to="/admin/access-control"
                  icon={Lock}
                  action="Open Access Control"
                  testId={DASH.cardAccessControl}
                />
              )}
            </div>
          </div>
        </section>

        <div className="emr-dash__lower">
          <section data-testid={DASH.clinicalFoundation} className="card emr-dash__section emr-dash-panel">
            <div className="card-content">
              <div className="emr-dash-panel__head">
                <span className="emr-dash-panel__icon" aria-hidden="true">
                  <HeartPulse className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="emr-dash__mod-title">Clinical Foundation</h2>
                  <p className="emr-dash__section-help" style={{ margin: 0 }}>Existing V3 resources</p>
                </div>
              </div>
              <div className="emr-dash-panel__list">
                {CLINICAL_FOUNDATION.filter((item) => (!item.adminOnly || isAdmin) && canAccessPath(user?.maintabAccess, item.to, tabOpts)).map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link key={item.to} to={item.to}>
                      <span className="emr-dash-glance__label">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {item.label}
                      </span>
                      <span className="emr-dash-panel__avail">Available</span>
                    </Link>
                  );
                })}
              </div>
              {canAccessPath(user?.maintabAccess, "/organizations", tabOpts) ? (
                <Link to="/organizations" className="btn-flat emr-dash-btn-block">
                  View All Resources
                </Link>
              ) : null}
            </div>
          </section>

          <section data-testid={DASH.nextAreas} className="card emr-dash__section emr-dash-panel">
            <div className="card-content">
              <div className="emr-dash-panel__head">
                <span className="emr-dash-panel__icon" aria-hidden="true">
                  <FileText className="h-4 w-4" />
                </span>
                <div>
                  <h2 className="emr-dash__mod-title">Next Development Areas</h2>
                  <p className="emr-dash__section-help" style={{ margin: 0 }}>Planned EMR building blocks</p>
                </div>
              </div>
              {NEXT_AREAS.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="emr-dash-planned">
                    <div className="emr-dash-planned__top">
                      <span className="emr-dash-glance__label">
                        <Icon className="h-4 w-4" aria-hidden="true" />
                        {item.title}
                      </span>
                      <span className="emr-dash__role">Planned</span>
                    </div>
                    <p>{item.description}</p>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn emr-dash-btn-block"
                onClick={() => toast.info("The development roadmap is planned and not yet available.")}
              >
                View Roadmap
              </button>
            </div>
          </section>
        </div>
      </div>

      <footer className="card-panel emr-dash__footer">
        <span className="emr-dash__footer-status">
          System status
          <span className="emr-mz-status__dot" aria-hidden="true" />
          All systems operational
        </span>
        <span>Version 3.1.1 | © {new Date().getFullYear()} EMRbyMD</span>
      </footer>
    </div>
  );
}
